import axios from '@/_utils/axios';
import { SEGMENT_KINDS_TO_PRESERVE } from '@/components/Packaging/constants';
import { BE2FEAssemblyTemplate, FE2BEOverlayItem, FE2BESegmentItem } from '@/components/Packaging/utils';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectCanCreateGenericTemplates } from '@/redux/selectors/authSelectors';
import { selectIsPackagingDraft } from '@/redux/selectors/packagingSelectors';
import { setActiveTemplate } from '@/redux/slices/packagingSlice';
import {
  AssemblyTemplateBERequest,
  AssemblyTemplateBEResponse,
  OverlayLayer,
  OverlayLayerBERequest,
  SegmentItem,
  TemplateType
} from '@/redux/slices/packagingSlice.types';
import { PolarisApiResponse } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { AxiosResponse } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Modal } from '../_common';
import ModalFooterButtons from '../_common/Modal/ModalFooterButtons';
import OverflowMenuItem from '../_common/OverflowMenu/OverflowMenuItem';
import PolarisIcon from '../_common/PolarisIcon';
import { useToast } from '../_common/Toast';

interface SaveTemplateProps {
  defaultName?: string;
  defaultType?: TemplateType;
  creationMode?: 'new' | 'save-as' | 'duplicate';
  overflow?: boolean;
  timeline?: {
    segments: SegmentItem[];
    overlays: OverlayLayer[];
  };
  autoApply?: boolean;
}

type SaveTemplatePayload = {
  name: string;
  description: string;
  template_type: TemplateType | null;
};

const templateTypeLabels: Record<TemplateType, string> = {
  generic: 'Generic Template',
  custom: 'Custom Template'
};

const DESCR_MAX_LENGTH = 300;

const AssemblyTemplateActionButton = ({
  defaultName,
  defaultType,
  creationMode = 'save-as',
  timeline,
  overflow = false,
  autoApply = false
}: SaveTemplateProps) => {
  const hasPermissions = useAppSelector(selectCanCreateGenericTemplates);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { success, error } = useToast();
  const { segments, overlays: overlaysLayers } = timeline ?? { segments: [], overlays: [[]] };
  const dispatch = useAppDispatch();
  const isDraft = useAppSelector(selectIsPackagingDraft);

  const history = useHistory();

  const defaultFormData: SaveTemplatePayload = useMemo(() => {
    return {
      name: defaultName ?? '',
      description: '',
      template_type: defaultType || null
    };
  }, [defaultName, defaultType]);

  const [formData, setFormData] = useState<SaveTemplatePayload>(defaultFormData);
  const isValidForm = useMemo(() => {
    const trimmedName = formData.name.trim();
    return (
      trimmedName.length > 0 &&
      trimmedName.length <= 100 &&
      formData.description.length <= DESCR_MAX_LENGTH &&
      (formData.template_type === TemplateType.Generic || formData.template_type === TemplateType.Custom)
    );
  }, [formData]);

  useEffect(() => {
    if (!hasPermissions) {
      setFormData({ ...formData, template_type: TemplateType.Generic });
    }
  }, [hasPermissions]);

  const handleSubmit = () => {
    setDialogOpen(false);

    const timelineStateWithoutBody: AssemblyTemplateBERequest['data'] =
      creationMode !== 'new'
        ? {
            overlays: overlaysLayers.map((layer): OverlayLayerBERequest => layer.map(FE2BEOverlayItem)),
            segments: segments.map(FE2BESegmentItem).map((segment) => {
              if (SEGMENT_KINDS_TO_PRESERVE.includes(segment.kind)) {
                // We don't want to save source of these segments
                return {
                  ...segment,
                  source: null
                };
              } else {
                return segment;
              }
            })
          }
        : {
            segments: [],
            overlays: [[]]
          };

    axios
      .post<
        PolarisApiResponse<AssemblyTemplateBEResponse>,
        AxiosResponse<PolarisApiResponse<AssemblyTemplateBEResponse>>,
        SaveTemplatePayload & { data: AssemblyTemplateBERequest['data'] }
      >('/assembly_templates/', {
        ...formData,
        data: timelineStateWithoutBody
      })
      .then((response) => {
        if (autoApply) {
          dispatch(setActiveTemplate(BE2FEAssemblyTemplate(response.data.data)));
        }

        if (creationMode === 'new' || creationMode === 'duplicate' || (creationMode === 'save-as' && !autoApply)) {
          history.push(`/assembly-templates/edit/${response.data.data.id}`);
        } else {
          success('Template saved successfully');
        }
      })
      .catch(() => {
        error('Error saving template');
      })
      .finally(() => {
        setFormData(defaultFormData);
      });
  };

  const handleCancel = () => {
    setFormData(defaultFormData);
    setDialogOpen(false);
  };

  return (
    <>
      {creationMode === 'duplicate' ? (
        overflow ? (
          <OverflowMenuItem onClick={() => setDialogOpen(true)}>
            <PolarisIcon icon="duplicate" height={15} />
            Duplicate
          </OverflowMenuItem>
        ) : (
          <StyledButton variant="outlined" color="primary" onClick={() => setDialogOpen(true)}>
            {'Duplicate Template'}
          </StyledButton>
        )
      ) : (
        <StyledButton variant="outlined" color="primary" onClick={() => setDialogOpen(true)} disabled={creationMode === 'save-as' && !isDraft}>
          {creationMode === 'new' ? 'New Template' : 'Save Template'}
        </StyledButton>
      )}
      <StyledModal
        icon={<PolarisIcon icon="settings" />}
        open={dialogOpen}
        title={creationMode === 'new' ? 'New Template' : creationMode === 'save-as' ? 'Save Template' : 'Duplicate Template'}
        onClose={handleCancel}
      >
        <div>
          <FormControl style={{ width: '100%' }}>
            <TextField
              label="Template Name"
              variant="filled"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              margin="dense"
              required
            />
            <TextField
              label="Template Description"
              variant="filled"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              margin="normal"
              error={formData.description.length > DESCR_MAX_LENGTH}
              helperText={formData.description.length ? `Description cannot exceed ${DESCR_MAX_LENGTH} characters` : ''}
            />
            <FormControl variant="filled" fullWidth margin="normal">
              <InputLabel>Template Type</InputLabel>
              <Select
                disabled={!hasPermissions}
                value={formData.template_type || ''}
                onChange={(e) => setFormData({ ...formData, template_type: e.target.value as TemplateType })}
                label="Template Type"
                required
              >
                <MenuItem value={TemplateType.Generic}>{templateTypeLabels.generic}</MenuItem>
                {hasPermissions && <MenuItem value={TemplateType.Custom}>{templateTypeLabels.custom}</MenuItem>}
              </Select>
            </FormControl>
          </FormControl>
          <ModalFooterButtons
            submit={{
              disabled: !isValidForm,
              onClick: handleSubmit
            }}
            cancel={handleCancel}
            divider={false}
          />
        </div>
      </StyledModal>
    </>
  );
};

export default AssemblyTemplateActionButton;

const StyledButton = styled(Button)`
  border: 1px solid ${({ theme }) => theme.palette.primary.main};
  margin-left: ${({ theme }) => theme.spacing(1)};

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.common.white};
  }
`;

const StyledModal = styled(Modal)`
  .MuiPaper-root {
    width: 500px;
  }
`;
